import Button from '@/components/Button';
import { NO_DATA } from '@/utils/constants';
import { hasPrism } from '@/utils/prescription';
import { RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { cylDiff, getADDData, moreThan5Diff, showGrayTd, sphDiff } from '../utils';
import UploadPrescriptionImg from './UploadPrescriptionImg';

export default function PrescriptionTableM({
  prescription,
  isReadOnly,
  showImage = false,
  showHeaderInfo = false,
  onDelete,
  onEdit,
  onUploadImg,
  showGray,
  showError,
}: {
  prescription: any;
  isReadOnly?: boolean;
  showImage?: boolean;
  showHeaderInfo?: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onUploadImg: (path: string) => void;
  showGray?: boolean;
  showError?: boolean;
}) {
  const t = useTranslations('account.myPrescriptions');
  // const innerTdClassName = 'border-auxiliary-light-gray-blue';
  const innerTdClassName = 'bg-white';
  const tableClassName = 'bg-[#f7f7f7]';
  const grayTdClassName = 'text-[#a9a9a9]';
  const errorTdClassName = 'text-primary-purple';

  const getPrescriptionType = (str: string, prescriptionTypeName: string) => {
    // Sunglasses Prescription 特殊处理
    if (str === 'Sunglasses Prescription') {
      return t('prescriptionTypes.sunglasses');
    }
    return prescriptionTypeName;
  };

  const PrescriptionImage = (
    <div className={`w-full row flex mt-10 ${innerTdClassName} border rounded-6`}>
      <div className="w-full h-[43px] text-center font-br flex items-center justify-between pr-12 pl-12  text-14">
        {t('prescriptionImage')}
        <UploadPrescriptionImg
          className="ml-10"
          img={prescription.image_url}
          onChange={onUploadImg}
          allowDelete={false}
          width={32}
          height={32}
        >
          <span className="flex items-center gap-4 text-[#00000066]">
            {t('uploadText')} <RightArrowOutlined className="text-[#00000066]" />
          </span>
        </UploadPrescriptionImg>
      </div>
    </div>
  );

  const renderPrescriptionImage = () => {
    if (isReadOnly) {
      return showImage && prescription.image_url ? PrescriptionImage : null;
    }
    return PrescriptionImage;
  };

  return (
    <div className="w-full flex justify-center items-center">
      <article
        className={`w-[95vw] prescription-card   bg-white rounded-6 ${
          isReadOnly ? 'p-0' : 'p-12 bg-[#f5f6f7]'
        }`}
      >
        {isReadOnly && !showHeaderInfo ? null : (
          <div className="flex justify-between items-center">
            <header className="flex flex-col">
              <strong className="w-full max-w-[200px] text-16 font-bs text-ellipsis truncate">
                {prescription.name}
              </strong>
              <time className="text-14 mt-4 font-br text-secondary-text">
                {prescription.updated_at}
              </time>
            </header>
            <div className="text-12  font-br text-primary-purple bg-[#0006cf1a] rounded-10 overflow-hidden pl-8 pr-8 py-2">
              {getPrescriptionType(
                prescription.prescription_type,
                prescription.prescription_type_name || prescription.prescription_type,
              )}
            </div>
          </div>
        )}
        <div
          className={`prescription-table border border-[#dce1e5] rounded-6 mt-10 ${tableClassName}`}
        >
          <div className="w-full row flex">
            <div className={`w-1/5 border-r`}>&nbsp;</div>
            <div
              className={`w-1/5 h-48 text-center font-bb text-14 flex items-center justify-center border-r`}
            >
              {t('table.sph')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-bb text-14 flex items-center justify-center border-r`}
            >
              {t('table.cyl')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-bb text-14 flex items-center justify-center border-r`}
            >
              {t('table.axis')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-bb text-14 flex items-center justify-center`}
            >
              {t('table.add')}
            </div>
          </div>

          {/* OD 右眼 */}
          <div className="w-full row flex">
            <div className={`w-1/5 border-r border-t pl-12 flex items-center`}>
              <span className="font-bs f14 inline-block ">
                <span>{t('table.od')}</span> <br />
                <span className="text-12 font-br text-secondary-text">{t('table.odHint')}</span>
              </span>
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showError && sphDiff(prescription.prescription) ? errorTdClassName : ''
              }`}
            >
              {prescription.prescription.od_sph || '0.00'}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showError && cylDiff(prescription.prescription) ? errorTdClassName : ''
              }`}
            >
              {prescription.prescription.od_cyl || '0.00'}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_axis') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.od_axis || NO_DATA}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center ${innerTdClassName} ${
                showGray &&
                getADDData(prescription.prescription.od_add, prescription.prescription_type) ===
                  NO_DATA
                  ? grayTdClassName
                  : ''
              }`}
            >
              {getADDData(prescription.prescription.od_add, prescription.prescription_type)}
            </div>
          </div>

          {/* OS 左眼 */}
          <div className="w-full row flex">
            <div className={`w-1/5 border-r border-t pl-12 flex items-center`}>
              <span className="font-bs f14 inline-block">
                <span>{t('table.os')}</span> <br />
                <span className="text-12 font-br text-secondary-text">{t('table.osHint')}</span>
              </span>
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showError && sphDiff(prescription.prescription) ? errorTdClassName : ''
              }`}
            >
              {prescription.prescription.os_sph || '0.00'}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showError && cylDiff(prescription.prescription) ? errorTdClassName : ''
              }`}
            >
              {prescription.prescription.os_cyl}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'os_axis') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.os_axis || NO_DATA}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center border-t ${innerTdClassName} justify-center ${
                showGray &&
                getADDData(prescription.prescription.os_add, prescription.prescription_type) ===
                  NO_DATA
                  ? grayTdClassName
                  : ''
              }`}
            >
              {getADDData(prescription.prescription.os_add, prescription.prescription_type)}
            </div>
          </div>

          {/* PD */}
          <div className="w-full row flex">
            <div className={`w-1/5 border-r border-t flex items-center pl-12`}>
              <span className="font-bs text-14 leading-0">{t('table.pd')}</span>
            </div>
            {prescription.prescription.pd && (
              <div
                className={`w-4/5 h-48 text-center font-br flex items-center justify-start pl-12 border-t ${innerTdClassName} rounded-br-[6px]`}
              >
                {prescription.prescription.pd}
              </div>
            )}
            {prescription.prescription.pd_l && prescription.prescription.pd_r && (
              <div
                className={`w-4/5 h-48 font-br flex items-center justify-around border-t ${innerTdClassName} rounded-br-[6px] ${
                  showError && moreThan5Diff(prescription.prescription) ? errorTdClassName : ''
                }`}
              >
                <span>
                  {' '}
                  {prescription.prescription.pd_r}{' '}
                  <span
                    className={`${
                      showError && moreThan5Diff(prescription.prescription)
                        ? errorTdClassName
                        : 'text-secondary-text'
                    }`}
                  >
                    {t('table.odHint')}
                  </span>
                </span>
                <span>
                  {' '}
                  {prescription.prescription.pd_l}{' '}
                  <span
                    className={`${
                      showError && moreThan5Diff(prescription.prescription)
                        ? errorTdClassName
                        : 'text-secondary-text'
                    }`}
                  >
                    {t('table.osHint')}
                  </span>
                </span>
              </div>
            )}
            {!prescription.prescription.pd_l &&
              !prescription.prescription.pd_r &&
              !prescription.prescription.pd && (
                <div
                  className={`w-4/5 h-48 font-br flex items-center justify-around border-t ${innerTdClassName}`}
                >
                  <span className="text-secondary-text" />
                </div>
              )}
          </div>
        </div>

        {hasPrism(prescription.prescription) && (
          <div
            className={`prescription-table border border-[#dce1e5] rounded-6 mt-10 ${tableClassName}`}
          >
            <div className="w-full row flex">
              <div className={`w-1/5 border-r`}>&nbsp;</div>
              <div
                className={`w-1/5 h-48 text-center text-12 font-bb flex items-center justify-center border-r`}
                dangerouslySetInnerHTML={{
                  __html: t('table.prismHorizontal'),
                }}
              />
              <div
                className={`w-1/5 h-48 text-center text-12 font-bb flex items-center justify-center border-r`}
                dangerouslySetInnerHTML={{
                  __html: t('table.baseDirection'),
                }}
              />
              <div
                className={`w-1/5 h-48 text-center text-12 font-bb flex items-center justify-center border-r`}
                dangerouslySetInnerHTML={{
                  __html: t('table.prismVertical'),
                }}
              />
              <div
                className={`w-1/5 h-48 text-center text-12 font-bb flex items-center justify-center`}
                dangerouslySetInnerHTML={{
                  __html: t('table.baseDirection'),
                }}
              />
            </div>

            {/* OD 右眼 */}
            <div className="w-full row flex">
              <div className={`w-1/5 border-r border-t pl-12 flex items-center`}>
                <span className="font-bs f14 inline-block">
                  <span>{t('table.od')}</span> <br />
                  <span className="text-12 font-br text-secondary-text">{t('table.odHint')}</span>
                </span>
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                  showGray && showGrayTd(prescription.prescription, 'od_pv') ? grayTdClassName : ''
                }`}
              >
                {prescription.prescription.od_pv || '0.00'}
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                  showGray && showGrayTd(prescription.prescription, 'od_bd') ? grayTdClassName : ''
                }`}
              >
                {prescription.prescription.od_bd
                  ? t(`table.${prescription.prescription.od_bd}`)
                  : '—'}
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                  showGray && showGrayTd(prescription.prescription, 'od_pv_r')
                    ? grayTdClassName
                    : ''
                }`}
              >
                {prescription.prescription.od_pv_r || '0.00'}
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center ${innerTdClassName} ${
                  showGray && showGrayTd(prescription.prescription, 'od_bd_r')
                    ? grayTdClassName
                    : ''
                }`}
              >
                {prescription.prescription.od_bd_r
                  ? t(`table.${prescription.prescription.od_bd_r}`)
                  : '—'}
              </div>
            </div>

            {/* OS 左眼 */}
            <div className="w-full row flex">
              <div className={`w-1/5 border-r border-t pl-12 flex items-center`}>
                <span className="font-bs f14 inline-block">
                  <span>{t('table.os')}</span> <br />
                  <span className="text-12 font-br text-secondary-text">{t('table.osHint')}</span>
                </span>
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                  showGray && showGrayTd(prescription.prescription, 'os_pv') ? grayTdClassName : ''
                }`}
              >
                {prescription.prescription.os_pv || '0.00'}
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                  showGray && showGrayTd(prescription.prescription, 'os_bd') ? grayTdClassName : ''
                }`}
              >
                {prescription.prescription.os_bd
                  ? t(`table.${prescription.prescription.os_bd}`)
                  : '—'}
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                  showGray && showGrayTd(prescription.prescription, 'os_pv_r')
                    ? grayTdClassName
                    : ''
                }`}
              >
                {prescription.prescription.os_pv_r || t('table.none')}
              </div>
              <div
                className={`w-1/5 h-48 text-center font-br flex items-center border-t ${innerTdClassName} justify-center rounded-br-[6px] ${
                  showGray && showGrayTd(prescription.prescription, 'os_bd_r')
                    ? grayTdClassName
                    : ''
                }`}
              >
                {prescription.prescription.os_bd_r
                  ? t(`table.${prescription.prescription.os_bd_r}`)
                  : '—'}
              </div>
            </div>
          </div>
        )}

        {renderPrescriptionImage()}

        {isReadOnly ? null : (
          <div className="w-full flex justify-end mt-18 gap-10">
            <Button
              variant="outlined"
              onClick={onDelete}
              sx={{
                width: '82px',
                height: '30px',
              }}
            >
              {t('delete')}
            </Button>

            <Button
              variant="contained"
              onClick={onEdit}
              sx={{
                width: '82px',
                height: '30px',
              }}
            >
              {t('edit')}
            </Button>
          </div>
        )}
      </article>
    </div>
  );
}
